import {
  GcfCommonComponentConfigInterface,
  GcfCommonComponentConfigSchema,
  SubHeaderConfig,
  SubHeaderConfigSchema,
  SubproductScaleType,
} from '../FormSelector.interface';

// visuals
interface ComboStyleVisualItemSourceBadgeInterface {
  visible: boolean;
  left: string;
  top: string;
  font: string;
  fontSize?: string;
  size?: string;
  color?: string;
  backgroundColor?: string;
}

interface OutageStampConfig {
  visible: boolean;
  url?: string;
  width?: string;
  height?: string;
  left?: string;
  top?: string;
}

export interface ComboStyleVisualItemSourceInterface {
  src: string;
  left: string;
  top: string;
  zIndex: number;
  height?: string;
  width?: string;
  badge?: ComboStyleVisualItemSourceBadgeInterface;
  outageStamp?: OutageStampConfig;
}
export interface ComboStyleVisualItemsInterface {
  linked: string;
  sources: ComboStyleVisualItemSourceInterface[];
}
export interface ComboStyleVisualInterface {
  items: ComboStyleVisualItemsInterface[];
}

// combo list
enum OrderLabelEnum {
  number = 'number',
  alphabet = 'alphabet',
}

export interface ComboStyleComboListGroupsInterface {
  targetGroup: string;
  productOrderOverride?: string;
  displayOrder?: boolean;
  offsetTop?: string;
  column?: number;
  groupHeader: SubHeaderConfig;
}

interface CommonTextStyleInterface {
  font?: string;
  fontSize?: string;
  color?: string;
  lineHeight?: string;
}

export interface BadgeOrderStyleInterface extends CommonTextStyleInterface {
  background?: string;
  size?: string;
  barWidth?: string;
}

interface ComboStyleComboListOrderLabelsInterface {
  groups: {
    orderType: OrderLabelEnum;
    orderFrom: string;
    orderStyle?: CommonTextStyleInterface;
  };

  product: {
    orderType: OrderLabelEnum;
    orderStyle?: BadgeOrderStyleInterface;
  };
}

interface ComboStyleProductListInterface {
  config: {
    badges: {
      groupBadge: ComboStyleProductListGroupBadgeInterface;
      itemBadge: ComboStyleProductListItemBadgeInterface;
    };
  };
}

interface ComboStyleProductListGroupBadgeInterface
  extends ComboStyleProductListItemBadgeInterface {
  startFrom: string;
}

export interface ComboStyleProductListItemBadgeInterface {
  type: OrderLabelEnum;
  style?: BadgeOrderStyleInterface;
}

export type CategoryConfigNameType = {
  [key: string]: ComboStyleComboListCategoryConfigInterface;
};

export interface ComboStyleComboListCategoryConfigInterface {
  nameOverride: string;
  displayLabel: boolean;
  stylesOverride: {
    subproducts: Partial<SubproductScaleType>;
  };
}

// schemas
const pxSchema = {
  type: 'string',
  anyOf: [
    {
      pattern: '^-?[0-9]*px$',
    },
    {
      pattern: '^$',
    },
  ],
};

const commonStyleSchema = {
  color: { type: 'string' },
  font: { type: 'string' },
  fontSize: pxSchema,
  lineHeight: pxSchema,
};

const groupsOrderStyleSchema = {
  type: 'object',
  properties: commonStyleSchema,
  additionalProperties: false,
  required: ['color', 'fontSize', 'font'],
};

const productOrderStyleSchema = {
  type: 'object',
  properties: {
    ...commonStyleSchema,
    background: {
      type: 'string',
    },
    size: {
      type: 'string',
    },
  },
  additionalProperties: false,
  required: ['color', 'fontSize', 'lineHeight', 'background', 'size'],
};

const comboStyleVisualItemsSchema = {
  type: 'array',
  items: {
    type: 'object',
    properties: {
      linked: {
        type: 'string',
        pattern: '^[0-9]*$',
      },
      sources: {
        type: 'array',
        items: [
          {
            type: 'object',
            properties: {
              src: { type: 'string' },
              height: pxSchema,
              width: pxSchema,
              left: pxSchema,
              top: pxSchema,
              zIndex: { type: 'number' },
              badge: {
                type: 'object',
                properties: {
                  visible: { type: 'boolean' },
                  font: { type: 'string' },
                  left: pxSchema,
                  top: pxSchema,
                  fontSize: pxSchema,
                  size: pxSchema,
                  color: { type: 'string' },
                  backgroundColor: { type: 'string' },
                },
                additionalProperties: false,
                required: ['visible'],
              },
              outageStamp: {
                type: 'object',
                properties: {
                  visible: { type: 'boolean' },
                  url: { type: 'string' },
                  top: pxSchema,
                  left: pxSchema,
                  width: pxSchema,
                  height: pxSchema,
                },
              },
            },
          },
        ],
        additionalProperties: false,
        required: ['src'],
      },
    },
    additionalProperties: false,
    required: ['linked', 'sources'],
  },
};

// combo list
const comboStyleComboListOrderLabelsSchema = {
  type: 'object',
  properties: {
    groups: {
      type: 'object',
      properties: {
        orderType: {
          enum: [OrderLabelEnum.number, OrderLabelEnum.alphabet],
        },
        orderFrom: {
          type: 'string',
        },
        orderStyle: groupsOrderStyleSchema,
      },
      additionalProperties: false,
      required: ['orderType', 'orderFrom'],
    },
    product: {
      type: 'object',
      properties: {
        orderType: {
          enum: [OrderLabelEnum.number, OrderLabelEnum.alphabet],
        },
        orderStyle: productOrderStyleSchema,
      },
      additionalProperties: false,
      required: ['orderType'],
    },
  },
};

const comboStyleComboListGroupsSchema = {
  type: 'array',
  items: {
    type: 'object',
    properties: {
      productOrderOverride: { type: 'string' },
      displayOrder: { type: 'boolean' },
      offsetTop: pxSchema,
      column: { type: 'number' },
      targetGroup: {
        type: 'string',
        pattern: '^group-[0-9]+$',
      },
      groupHeader: SubHeaderConfigSchema,
    },
    additionalProperties: false,
    required: ['targetGroup'],
  },
};

const comboStyleComboListCategoryConfigSchema = {
  type: 'object',
  patternProperties: {
    // find dynamic way
    '^.*$': {
      type: 'object',
      properties: {
        nameOverride: { type: 'string' },
        displayLabel: { type: 'boolean' },
        stylesOverride: {
          type: 'object',
          properties: {
            subproducts: {
              type: 'object',
              properties: {
                label: {
                  type: 'object',
                  properties: {
                    ...commonStyleSchema,
                    border: { type: 'string' },
                    backgroundColor: { type: 'string' },
                  },
                },
                price: {
                  type: 'object',
                  properties: commonStyleSchema,
                },
                nutrition: {
                  type: 'object',
                  properties: commonStyleSchema,
                },
              },
            },
          },
          additionalProperties: false,
        },
      },
    },
  },
  additionalProperties: false,
};

const productListStyleSchema = {
  type: 'object',
  properties: {
    ...commonStyleSchema,
    background: {
      type: 'string',
    },
    size: {
      type: 'string',
    },
  },
  additionalProperties: true,
};

export const ComboStyleProductListItemBadgeSchema = {
  type: 'object',
  properties: {
    type: { type: 'string' },
    style: productListStyleSchema,
  },
  additionalProperties: false,
  required: ['type'],
};

export const ComboStyleConfigSchema = {
  title: 'Combo Style JSON Config',
  description: 'JSON Configuration Options for GCF Combo Style',
  type: 'object',
  properties: {
    ...GcfCommonComponentConfigSchema.properties,
    visuals: {
      type: 'object',
      properties: {
        items: comboStyleVisualItemsSchema,
      },
      additionalProperties: false,
      required: ['items'],
    },
    comboList: {
      type: 'object',
      properties: {
        orderLabels: comboStyleComboListOrderLabelsSchema,
        groups: comboStyleComboListGroupsSchema,
        categoryConfig: comboStyleComboListCategoryConfigSchema,
      },
      additionalProperties: true,
      required: ['orderLabels'],
    },
    productList: {
      type: 'object',
      properties: {
        config: {
          type: 'object',
          properties: {
            badges: {
              type: 'object',
              properties: {
                itemBadge: ComboStyleProductListItemBadgeSchema,
              },
            },
          },
        },
      },
      additionalProperties: true,
    },
    lineupColumns: {
      type: 'number',
    },
    marginBottom: { type: 'number' },
  },
  required: [...GcfCommonComponentConfigSchema.required],
  additionalProperties: true,
};

export interface ComboStyleConfigInterface
  extends GcfCommonComponentConfigInterface {
  visuals: ComboStyleVisualInterface;
  lineupColumns?: number;
  comboList: ComboStyleComboListInterface;
  productList?: ComboStyleProductListInterface;
}
interface ComboStyleComboListInterface {
  orderLabels: ComboStyleComboListOrderLabelsInterface;
  groups?: ComboStyleComboListGroupsInterface[];
  categoryConfig?: CategoryConfigNameType;
  productInfoStyling?: ComboStyleComboListCategoryConfigInterface; // used only on CC-UI form for checked field for product styling
}
